import React from "react";

import { UseFormReturn } from "react-hook-form";
import * as Yup from "yup";

import {
    CustomerFormProps,
    CustomerFormState
} from "./models";

import {
    CheckVatResponseObject,
    Customer,
    MainConstants
} from "../../../pages/models";

import {
    Button,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    MenuItem
} from "@mui/material";

import {
    Loading,
    MatesDatePicker,
    MatesSelect,
    MatesSwitch,
    MatesTextField,
    withUseFormHook,
} from "@sinossi/mates-react-library";

import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import PhoneIcon from "@mui/icons-material/Phone";

import {
    t,
    Trans
} from "@lingui/macro";

import {
    withSnackbar,
    WithSnackbarProps
} from "notistack";
import { CustomersService } from "../../../services/customers.service";
import InfoDialog from "../../shared/info-dialog/info-dialog";
import "./customer-form.css";

import { getCountries, getCountryCallingCode, CountryCode } from "libphonenumber-js";
import { DateTime } from "luxon";
import { FiscalCodeValidator } from "../../../utils/fiscal-code-validator";

const phoneRegExp = /^\+?\d{7,}$/;

export const CustomerSchema = Yup.object().shape({

    fiscalCode: Yup.string().required(t({
        id: "cf-cs-codfiscale-required-msg",
        message: "Il codice fiscale è obbligatorio"
    })).test("personal-data-match", t({
        id: "cf-cs-fiscal-code-mismatch-msg",
        message: "Il codice fiscale non corrisponde a nome, cognome e data di nascita. Ricontrolla i dati inseriti."
    }), function (value) {
        const { legalNature, name, surname, birthDate } = this.parent;
        if (!["PF", "DI", "PP"].includes(legalNature) || !value || !name?.trim() || !surname?.trim()
            || !FiscalCodeValidator.isValidBirthDate(birthDate)) return true;
        return FiscalCodeValidator.matches(value, name, surname, birthDate);
    }),
    birthDate: Yup.mixed().when("legalNature", {
        is: (nature: string) => ["PF", "DI", "PP"].includes(nature),
        then: Yup.mixed().required(t({
            id: "cf-cs-birth-date-required-msg",
            message: "La data di nascita è obbligatoria"
        })).test("valid-birth-date", t({
            id: "cf-cs-birth-date-invalid-msg",
            message: "Inserisci una data di nascita valida, non futura"
        }), value => FiscalCodeValidator.isValidBirthDate(value)),
        otherwise: Yup.mixed().nullable()
    }),
    name: Yup.string().nullable().when("legalNature", {
        is: (nature: string) => ["PF", "DI", "PP"].includes(nature),
        then: Yup.string().trim().required(t({
            id: "cf-cs-person-name-required-msg",
            message: "Il nome è obbligatorio"
        }))
    }),
    surname: Yup.string().nullable().when("legalNature", {
        is: (nature: string) => ["PF", "DI", "PP"].includes(nature),
        then: Yup.string().trim().required(t({
            id: "cf-cs-person-surname-required-msg",
            message: "Il cognome è obbligatorio"
        }))
    }),
    denomination: Yup.string().nullable().when("legalNature", {
        is: (nature: string) => !["PF", "DI", "PP"].includes(nature),
        then: Yup.string().trim().required(t({
            id: "cf-cs-denomination-required-msg",
            message: "La denominazione è obbligatoria"
        }))
    }),
    email: Yup.string().email(t({
        id: "cf-cs-email-valid-msg",
        message: "Inserire un indirizzo e-mail valido"
    })).required(t({
        id: "cf-cs-email-required-msg",
        message: "L'indirizzo e-mail è obbligatorio"
    })),
    legalNature: Yup.string().required(t({
        id: "cf-cs-legal-nature-required-msg",
        message: "La natura giuridica è obbligatoria"
    })),
    mainActivity: Yup.string().required(t({
        id: "cf-cs-main-activity-required-msg",
        message: "La prevalente attività è obbligatoria"
    })),
    geoArea: Yup.string().required(t({
        id: "cf-cs-geo-area-required-msg",
        message: "L'area di residenza è obbligatoria"
    })),
    contactPhone: Yup.string()
        .nullable()
        .matches(phoneRegExp, {
            message: t({
                id: "cf-cs-phone-number-valid-msg",
                message: "Inserire un numero di telefono valido"
            }),
            excludeEmptyString: true // Esclude la stringa vuota dalla validazione regex
        }),
    contactName: Yup.string().required(t({
        id: "cf-cs-name-required-msg",
        message: "Il nome di contatto è obbligatorio"
    })),
    contactSurname: Yup.string().required(t({
        id: "cf-cs-surname-required-msg",
        message: "Il cognome di contatto è obbligatorio"
    })),

});

class CustomerForm extends React.Component<CustomerFormProps & UseFormReturn & WithSnackbarProps, CustomerFormState> {

    private phoneCountry: CountryCode = "IT";
    private originalPhone: string | null | undefined;
    private phoneChanged = false;
    private formSubscription: { unsubscribe: () => void } | null = null;

    /** Splits an international number into its country selector and national digits. */
    private splitPhone = (value: string): string => {
        const normalized = value.replace(/[\s()\-]/g, "").replace(/^00/, "+");
        if (!normalized.startsWith("+")) return normalized;
        const countries = getCountries().sort((a, b) =>
            getCountryCallingCode(b).length - getCountryCallingCode(a).length);
        const country = countries.find(country => normalized.startsWith("+" + getCountryCallingCode(country)));
        if (!country) return normalized;
        const code = getCountryCallingCode(country);
        this.phoneCountry = code === "39" ? "IT" : country;
        return normalized.slice(code.length + 1);
    };

    /** Combines edited phone digits and the selected prefix while preserving untouched stored values. */
    private combinedPhone = (value: string): string | null => {
        if (!this.phoneChanged && this.props.customer) return this.originalPhone ?? null;
        return value ? "+" + getCountryCallingCode(this.phoneCountry) + value : value;
    };

    private viesTimeout: any = null;

    /** Initializes the customer creation form. */
    constructor(props: any) {
        super(props);

        this.state = {
            insertedCustomer: null,
            isLoading: true,
            isMainActivityInfoModalOpen: false,
            isContactDataChange: false
        };
    }

    /**
     * Guards personal fiscal-code data before checking duplicates and retaining the birth date for onboarding.
     */
    private submitForm = (values: any) => {

        let pfOrDiCheck = values.legalNature === "PF" || values.legalNature === "DI" || values.legalNature === "PP";

        if (pfOrDiCheck && !FiscalCodeValidator.matches(values.fiscalCode, values.name, values.surname, values.birthDate)) {
            this.props.setError("fiscalCode", {
                type: "manual",
                message: t({
                    id: "cf-cs-fiscal-code-mismatch-msg",
                    message: "Il codice fiscale non corrisponde a nome, cognome e data di nascita. Ricontrolla i dati inseriti."
                })
            });
            return;
        }

        let customer: Customer = {
            additionalInfo: {
                collaborative: values.collaborative != null ? values.collaborative : false,
                mainActivity: values.mainActivity,
                ateco2007: null,
                constitutionDate: null,
                birthDate: pfOrDiCheck ? values.birthDate : null,
                address: null,
                businessType: null,
                authCode: null,
                advDuration: null,
                requiredDocuments: null
            },
            deleted: null,
            deletedAt: null,
            deletedBy: null,
            efestoProcessId: null,
            fiscalCode: pfOrDiCheck ? values.fiscalCode.trim().toUpperCase() : values.fiscalCode,
            id: null,
            name: pfOrDiCheck ? values.name : null,
            office: null,
            ownerList: null,
            pec: null,
            status: null,
            surname: pfOrDiCheck ? values.surname : null,
            vatCode: null,
            verificationExpireDate: null,
            verificationType: null,
            email: values.email,
            denomination: !pfOrDiCheck ? values.denomination : null,
            contactName: values.contactName,
            contactSurname: values.contactSurname,
            contactPhone: this.combinedPhone(values.contactPhone),
            legalNature: values.legalNature,
            geoArea: values.geoArea,
            managementCode: values.managementCode,
            worker: null,
            documentList: null
        };

        this.setState(() => ({
            insertedCustomer: customer,
            isLoading: true
        }));

        CustomersService.checkCustomerExists(customer, this.checkCustomerExistsCallback);
    };

    /** Restores customer fields and revalidates the fiscal code when identity data changes. */
    componentDidMount() {

        this.setState(() => ({ isLoading: true }));

        if (this.props.customer !== null) {
            this.originalPhone = this.props.customer.contactPhone;
            const savedBirthDate = this.props.customer.additionalInfo?.birthDate;
            this.props.reset({
                ...this.props.customer,
                contactPhone: this.splitPhone(this.props.customer.contactPhone || ""),
                mainActivity: this.props.customer.additionalInfo?.mainActivity,
                collaborative: this.props.customer.additionalInfo?.collaborative,
                birthDate: typeof savedBirthDate === "string"
                    ? DateTime.fromISO(savedBirthDate, { setZone: true })
                    : savedBirthDate instanceof Date ? DateTime.fromJSDate(savedBirthDate) : savedBirthDate ?? null
            });
        }

        if (!this.props.readOnly && this.props.watch) {
            this.formSubscription = this.props.watch((_, { name }) => {
                if (name && ["legalNature", "name", "surname", "birthDate"].includes(name))
                    this.props.trigger("fiscalCode");
            });
        }

        this.setState(() => ({ isLoading: false }));
    }

    /** Stops listening for form edits and clears any pending VAT lookup. */
    componentWillUnmount() {
        this.formSubscription?.unsubscribe();
        if (this.viesTimeout !== null) clearTimeout(this.viesTimeout);
    }

    private checkCustomerExistsCallback = (response: any) => {

        this.setState(() => ({ isLoading: false }));

        console.log(response);

        if (response.status !== 200) {
            this.props.enqueueSnackbar(t({
                id: "cf-error-customer-exists-error",
                message: "Il cliente inserito esiste già"
            }), {
                variant: "error",
            });
        } else {
            this.props.onCustomerSubmit(this.state.insertedCustomer);
        }
    };

    setMainActivityInfoModalVisibility = () => {
        this.setState(() => ({ isMainActivityInfoModalOpen: !this.state.isMainActivityInfoModalOpen }));
    };

    updateDataCallback = (response: any) => {

        if (!response.hasError()) {
            this.setState(() => ({ isContactDataChange: false }));
        } else {
            this.props.enqueueSnackbar(t({
                id: "cf-error-update-contact-data",
                message: "Errore nel salvataggio dei dati"
            }), {
                variant: "error",
            });
        }
    }


    /** Looks up VAT data only for non-personal customer types. */
    private callVies = (vatCode: string) => {

        if (this.viesTimeout !== null) {
            clearTimeout(this.viesTimeout)
        }
        if (["PF", "DI", "PP"].includes(this.props.getValues("legalNature"))) return vatCode;
        this.viesTimeout = setTimeout(() => {
            if (/^\d{11}$/.test(vatCode)) {
                vatCode = 'IT' + vatCode
            }
            let isValid = this.validateItVatCode(vatCode)

            if (isValid) {
                this.setState(() => ({ isLoading: true }))
                CustomersService.checkViesVatCode(vatCode.substr(2), this.viesCallback)
            }

        }, 500)

        return vatCode;
    }

    private readonly validateItVatCode = (vatCode: string) => {

        if (vatCode.startsWith("IT")) {
            let vatNumber = vatCode.substr(2)
            let v = Array(...Array(11))
            const sum: number = v
                .map((_, it: number) => CustomerForm._getDigit(vatNumber.charAt(it), it))
                .reduce((a: number, b: number) => a + b)

            return sum % 10 === 0
        }
        return false
    }

    private static readonly _getDigit = (c: string, i: number) => {
        let digit = c.charCodeAt(0) - "0".charCodeAt(0)
        if (i % 2 === 0) {
            return digit
        }

        digit *= 2
        if (digit > 9) {
            digit -= 9
        }

        return digit
    }


    private readonly viesCallback = (data: CheckVatResponseObject) => {

        if (data?.data?.valid && data?.data?.name?.value) {
            this.props.setValue("denomination", data.data.name.value)
        }
        this.setState(() => ({ isLoading: false }))
    }

    /** Saves contact details with the selected international phone prefix. */
    private readonly saveChanges = () => {

        let request = {
            name: this.props.getValues().contactName,
            surname: this.props.getValues().contactSurname,
            phone: this.combinedPhone(this.props.getValues().contactPhone),
            email: this.props.getValues().email,
            managementCode: this.props.getValues().managementCode
        };

        CustomersService.updateContactData(this.props.customer?.id, request, this.updateDataCallback);
    }

    /** Renders client fields and an active submit button so validation errors can be shown. */
    render() {

        let pfOrDiCheck = this.props.getValues().legalNature === "PF" || this.props.getValues().legalNature === "DI" || this.props.getValues().legalNature === "PP";

        let currentLanguage = window.localStorage.getItem("selected_language") || "IT";

        return (<>
            {this.state.isMainActivityInfoModalOpen && <InfoDialog simpleText={t({
                id: "cf-main-activity-info-text",
                message: "Lorem ipsum dolor sit amet"
            })} title={t({
                id: "cf-main-activity-info-title",
                message: "Prevalente attività"
            })}
                closingButtonAction={this.setMainActivityInfoModalVisibility} />}
            {this.state.isLoading && <Loading show />}
            {!this.state.isLoading && <form onSubmit={this.props.handleSubmit(this.submitForm)}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <MatesSelect
                            variant={"outlined"}
                            name="legalNature"
                            label={t({
                                id: "cf-legal-nature",
                                message: "Natura Giuridica"
                            })}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={this.props.readOnly}>

                            {MainConstants.CUSTOMER_LEGAL_NATURE_LIST.map(
                                (item) => (<option value={item.key}
                                    key={item.key}>{currentLanguage === "IT" ? item.label : item.labelEn}</option>))}

                        </MatesSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <MatesTextField
                            variant={"outlined"}
                            name="fiscalCode"
                            label={t({
                                id: "cf-fiscal-code",
                                message: "Codice fiscale"
                            })}
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState}
                            disabled={this.props.readOnly}
                            onChange={this.callVies}
                        />
                    </Grid>
                    {!pfOrDiCheck && <Grid item xs={12}>
                        <MatesTextField
                            variant={"outlined"}
                            name="denomination"
                            label={t({
                                id: "cf-denomination",
                                message: "Denominazione"
                            })}
                            control={this.props.control}
                            trigger={this.props.trigger}
                            formState={this.props.formState}
                            disabled={this.props.readOnly} />
                    </Grid>}
                    {pfOrDiCheck && <>
                        <Grid item xs={6}>
                            <MatesTextField
                                variant={"outlined"}
                                name="name"
                                label={t({
                                    id: "cf-name",
                                    message: "Nome"
                                })}
                                control={this.props.control}
                                trigger={this.props.trigger}
                                formState={this.props.formState}
                                disabled={this.props.readOnly} />
                        </Grid>
                        <Grid item xs={6}>
                            <MatesTextField
                                variant={"outlined"}
                                name="surname"
                                label={t({
                                    id: "cf-surname",
                                    message: "Cognome"
                                })}
                                control={this.props.control}
                                trigger={this.props.trigger}
                                formState={this.props.formState}
                                disabled={this.props.readOnly} />
                        </Grid>
                        {!this.props.readOnly && <Grid item xs={6}>
                            <MatesDatePicker
                                variant="outlined"
                                name="birthDate"
                                label={t({
                                    id: "cf-birth-date",
                                    message: "Data di nascita"
                                })}
                                control={this.props.control}
                                trigger={this.props.trigger}
                                formState={this.props.formState}
                                maxDate={DateTime.local()}
                                disabled={this.props.readOnly} />
                        </Grid>}
                    </>}
                    <Grid item xs={6}>
                        <MatesSelect
                            name="geoArea"
                            variant={"outlined"}
                            label={t({
                                id: "cf-geo-area",
                                message: "Area Geografica di residenza"
                            })}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={this.props.readOnly}>

                            {MainConstants.GEO_AREA.map((item) => (<option value={item.key}
                                key={item.key}>{currentLanguage === "IT" ? item.label : item.labelEn}</option>))}
                        </MatesSelect>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack direction={"row"} spacing={1} style={{
                            display: "inline-flex",
                            alignItems: "center",
                            width: "100%"
                        }}>
                            <MatesSelect
                                name={!this.props.readOnly ? "mainActivity" : "additionalInfo.mainActivity"}
                                variant={"outlined"}
                                label={t({
                                    id: "cf-main-activity",
                                    message: "Prevalente attività"
                                })}
                                control={this.props.control}
                                formState={this.props.formState}
                                trigger={this.props.trigger}
                                disabled={this.props.readOnly}>
                                {MainConstants.CUSTOMER_MAIN_ACTIVITY.map(
                                    (item) => (<option value={item.key}
                                        key={item.key}>{currentLanguage === "IT" ? item.label : item.labelEn}</option>))}
                            </MatesSelect>
                            <InfoIcon
                                sx={{
                                    color: "blue",
                                    cursor: "pointer"
                                }}
                                onClick={this.setMainActivityInfoModalVisibility}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <MatesSwitch
                            name={!this.props.readOnly ? "collaborative" : "additionalInfo.collaborative"}
                            label={t({
                                id: "cf-collaborative",
                                message: "Il cliente si è dimostrato collaborativo e trasparente"
                            })}
                            control={this.props.control}
                            formState={this.props.formState}
                            disabled={this.props.readOnly}
                            trigger={this.props.trigger} />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="contactName"
                            variant={"outlined"}
                            label={t({
                                id: "cf-contact-name",
                                message: "Nome di contatto"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="contactSurname"
                            variant={"outlined"}
                            label={t({
                                id: "cf-contact-surname",
                                message: "Cognome di contatto"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={1} sx={{ "& > :last-child": { flex: 1, minWidth: 0 } }}>
                        <TextField select value={this.phoneCountry} label={t({id: "cf-phone-prefix", message: "Prefisso"})}
                            sx={{width: 130, flexShrink: 0}}
                            SelectProps={{ renderValue: (value) => { const country = value as CountryCode; return String.fromCodePoint(...country.split("").map(c => c.charCodeAt(0) + 127397)) + " +" + getCountryCallingCode(country); } }}
                            onChange={(event) => { this.phoneCountry = event.target.value as CountryCode; this.phoneChanged = true; this.setState({isContactDataChange: true}); }}>
                            {getCountries().sort((a, b) => new (Intl as unknown as { DisplayNames: new (locales: string[], options: {type: string}) => {of(code: string): string} }).DisplayNames([currentLanguage.toLowerCase()], {type: "region"}).of(a)!.localeCompare(new (Intl as unknown as { DisplayNames: new (locales: string[], options: {type: string}) => {of(code: string): string} }).DisplayNames([currentLanguage.toLowerCase()], {type: "region"}).of(b)!)).map(country =>
                                <MenuItem key={country} value={country}>{String.fromCodePoint(...country.split("").map(c => c.charCodeAt(0) + 127397))} {new (Intl as unknown as { DisplayNames: new (locales: string[], options: {type: string}) => {of(code: string): string} }).DisplayNames([currentLanguage.toLowerCase()], {type: "region"}).of(country)} +{getCountryCallingCode(country)}</MenuItem>)}
                        </TextField>
                        <MatesTextField
                            name="contactPhone"
                            variant={"outlined"}
                            label={t({
                                id: "cf-phone",
                                message: "Cellulare"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                this.phoneChanged = true;
                                return this.splitPhone(e)
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                            endAdornment={<InputAdornment position="end">
                                <PhoneIcon />
                            </InputAdornment>}
                        />
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="email"
                            variant={"outlined"}
                            label={t({
                                id: "cf-email",
                                message: "Indirizzo e-mail"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                            endAdornment={<InputAdornment position="end">
                                <EmailIcon />
                            </InputAdornment>}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MatesTextField
                            name="managementCode"
                            variant={"outlined"}
                            label={t({
                                id: "cf-management-code",
                                message: "Codice Gestionale"
                            })}
                            onChange={(e) => {
                                this.setState(() => ({ isContactDataChange: true }));
                                return e;
                            }}
                            control={this.props.control}
                            formState={this.props.formState}
                            trigger={this.props.trigger}
                            disabled={false}
                        />
                    </Grid>
                    {!this.props.readOnly && <Grid item xs={12} textAlign="right">
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={this.props.formState.isSubmitting || this.state.isLoading}
                            type="submit">
                            <Trans id={"cf-go-ahead"}>
                                Avanti
                            </Trans>
                        </Button>
                    </Grid>}
                    {this.props.readOnly && <Grid item xs={12} textAlign="right">
                        <Button
                            onClick={this.saveChanges}
                            color="primary"
                            variant="contained"
                            disabled={!this.state.isContactDataChange}>
                            <Trans id={"cf-save"}>Salva</Trans>
                        </Button>
                    </Grid>}
                </Grid>
            </form>}
        </>);
    }
}

const componentWithForm = withUseFormHook<{}>(CustomerForm, CustomerSchema, {
    mainActivity: "NORMAL",
    geoArea: "ITALY"
});

export default withSnackbar(componentWithForm);
